/* jshint esnext: true */
import AbstractModule from './AbstractModule';

import tap from '../ractive/ractive-events-tap';

import { unescapeHtml } from '../utils/html';

export default class extends AbstractModule {
    constructor (options) {
        super(options);

        this.calendar = null;

        this.controller();
        this.initCalendar();

        this.registerEvents();

        this.$document.on('updateEventList.EventList', (event) => {
            this.controller().fire('updateEventList', {
                events: event.events,
                month: event.month
            });
        });
    }

    /**
     * Getter and setter combo for Ractive event list controller.
     * @param  {object} data     CLNDR data provided on render() callback
     * @return {Ractive object}  Ractive instance
     */
    controller() {
        if (typeof this._controller === 'undefined') {
            this._controller = this.initController();
        }

        return this._controller;
    }

    /**
     * Creates the Ractive event list controller
     * @return {Ractive object}  Ractive instance
     */
    initController() {
        var self = this;
        var controller = new Ractive({
            el: this.$el.find('.js-container').get(0),
            template: unescapeHtml(this.$el.find('.js-template:first').html()),
            delimiters: [ '[[', ']]' ],
            data: {
                activeCategory: '',
                activeLocation: '',

                activeMonth: window.templateData.activeMonth,
                activeYear: window.templateData.activeYear,

                categoryList: window.templateData.categoryList,
                eventList: window.templateData.eventList,
                locationList: window.templateData.locationList,

                filteredEventList: [],
                monthEventList: [],

                eventsPerPage: 10,
                page: 1,

                state: 'inert'
            },
            computed: {
                hasMoreEvents: function () {
                    return this.get('page') < this.maxPages();
                },
                isDateFiltered: function () {
                    return !!this.get('monthEventList').length;
                },
                pageArray: function () {
                    // represents the events that are associated with the selected month
                    var month = this.get('monthEventList');

                    if (month.length) {
                        return {0:{
                            events: month
                        }};
                    }

                    var filteredEventList = this.get('filteredEventList');
                    var eventsPerPage = this.get('eventsPerPage');
                    var pageArray = [];
                    var i = 0;
                    var len = this.get('page');
                    for (; i < len; i++) {
                        let min = eventsPerPage * i;
                        let max = (eventsPerPage * i) + eventsPerPage;
                        let page = filteredEventList.slice(min, max);
                        pageArray[i] = {
                            events: page
                        };
                    }
                    return pageArray;
                }
            },
            partials: {
                image: '<div class="o-background" style="background-image: url({{image}})"></div>'
            },
            events: { tap },
            /**
             * Used for displaying a filtered (by category) event list on front-end
             * @param  {string} filterKeypath The filter being activated
             * @return {array}
             */
            filteredEventList: function(filterKeypath) {
                if (typeof filterKeypath !== 'undefined' && filterKeypath !== '') {
                    this.set((filterKeypath === 'activeCategory' ? 'activeLocation' : 'activeCategory'), '');
                }

                var category = this.get('activeCategory');
                var location = this.get('activeLocation');
                var list = this.get('eventList').slice();
                var isCategory = !(category === '' || category === undefined || category === null);
                var isLocation = !(location === '' || location === undefined || location === null);

                self.resetCalendar();

                // If both filters are empty
                if (isCategory || isLocation) {
                    list =  list.filter(function(item) {
                        // In case of multiple categories per event
                        // return arrayContains(item.categories, category);
                        return isCategory ? item.category === category : item.locationId === location;
                    });
                }

                self.calendar.setEvents(list);

                return list;
            },
            /**
             * Calculate the max pages possible for featuredeventList
             * @return {int}
             */
            maxPages: function() {
                var filteredEventsCount = this.get('filteredEventList').length;
                var eventsPerPage = this.get('eventsPerPage');
                var remainder = (filteredEventsCount % eventsPerPage);
                return (filteredEventsCount - remainder) / eventsPerPage + (remainder !== 0 ? 1 : 0);
            },
            /**
             * Allows us to set proxy events and run other tasks when controller is initialized
             * @param  {array}  options  Array of options
             */
            oninit: function(options) {
                self.$document.trigger('refreshSkrollr.Parallax');
                this.fire('updateFilteredEventList');
            },
            /**
             * Swich values that are toggled between two states on load state
             * @param  {boolean}  state  Controls towards which state the animation goes
             *                           True -> display list
             *                           False -> hide list
             * @return {Promise}         Allows chaining with a .then()
             */
            toggleLoading: function(state) {
                var values = (typeof state === 'boolean' && state === false) ?
                {
                    state: 'loading'
                } : {
                    state: 'inert'
                };

                self.closeCalendar();

                return this.set(values);
            }
        });

        /**
         * Fired by the load more button
         */
        controller.on('loadNextPage', function() {
            this.add('page');
        });

        /**
         * Fired by the load more button
         */
        controller.on('removeDateFilter', function() {
            this.toggleLoading(false).then(() => {
                setTimeout(() => {
                    self.resetCalendar();
                    this.set({
                        monthEventList: []
                    }).then(() => {
                        this.toggleLoading(true);
                    });
                }, 600);
            });
        });

        /*
         * Programmatically fired by the calendar nav module
         * Do not use in template, as `data` will end up being the Ractive event object
         * @param {object} data  Object containing a list of events and a moment.js object set to the active month.
         */
        controller.on('updateEventList', function(data) {
            // Trigger loading transitions
            this.toggleLoading(false).then(() => {
                // Small timeout for animations
                setTimeout(() => {
                    // // Test for empty events response. If so, keep old events
                    var events = data.events;

                    // We can now update the eventList data store
                    this.set({
                        activeMonth: data.month.format('MMMM'),
                        activeYear: data.month.format('YYYY'),
                        eventList: events,
                        page: 1
                    }).then(() => {
                        // Once that is done, we can filter the eventList and redisplay the front-end
                        this.set('filteredEventList', this.filteredEventList()).then(() => {
                            this.toggleLoading(true);
                        });
                    });
                }, 600);
            });
        });

        /**
         * Fired by the category input observer
         * @param  {string} filterKeypath
         */
        controller.on('updateFilteredEventList', function (filterKeypath) {
            this.fire('removeDateFilter');
            this.toggleLoading(false).then(() => {
                setTimeout(() => {
                    this.set({
                        filteredEventList: this.filteredEventList(filterKeypath),
                        page: 1
                    }).then(() => {
                        this.toggleLoading(true)
                    });
                }, 600);
            });
        });

        controller.observe('activeCategory activeLocation', function(newValue, oldValue, keypath) {
            if (this.get('state') === 'inert') {
                this.fire('updateFilteredEventList', keypath);
            } else {
                // if (newValue !== oldValue) {
                //     console.log('shit');
                //     this.set(keypath, oldValue);
                // }
            }
        });

        return controller;
    }

    initCalendar() {
        //init plugin calendar
        this.calendar = $('.js-date-picker', this.$el).calendar({
            mode               : 'month',
            allowDateView      : false,
            allowMonthView     : true,
            displayEventsNumber: true,
            lang               : 'fr',
            translations       : {
                months        : {
                    fr: ['jan', 'fev', 'mar', 'avr', 'mai', 'jun', 'jul', 'aou', 'sep', 'oct', 'nov', 'dec']
                },
                nextMonthLabel: {
                    fr: '&raquo;'
                },
                prevMonthLabel: {
                    fr: '&laquo;'
                },
                nextYearLabel : {
                    fr: '<p>&raquo;</p>'
                },
                prevYearLabel : {
                    fr: '<p>&laquo;</p>'
                }
            },
            callbacks          : {
                onMonthSelect: (date, calendar) => this.eventClick(date, calendar)
            }
        });

        this.resetCalendar();
        let events = this.controller().get('eventList');
        this.calendar.setEvents(events);
    }

    resetCalendar() {
        this.calendar.setSelectedDate(null);
        this.calendar.refresh();
    }

    eventClick(date, calendar) {
        let events = this.calendar.getEventsByMonth(date);
        let month = calendar.aMonths[calendar.lang][date.getMonth()];

        this.controller().toggleLoading(false).then(() => {
            setTimeout(() => {
                this.controller().set({
                    monthEventList: events,
                    activeMonth: month
                }).then(() => {
                    this.controller().toggleLoading(true);
                });
            }, 600);
        });
    }

    /*
     *  hide the calendar
     */
    closeCalendar() {
        this.$el.removeClass('calendar-is-open');
    }

    /*
     *  Display the calendar on click to the date picker button
     */
    toggleCalendar(e) {
        this.$el.toggleClass('calendar-is-open');
        if (this.$el.hasClass('calendar-is-open')) {
            this.$document.trigger('openCalendar');
        }
    }

    registerEvents() {
        this.$el.on('click.EventList', '.js-date-picker-button', (e) => this.toggleCalendar(e));
    }

    destroy() {
        if (this.controller()) {
            this.controller().teardown();
        }
        this.$document.off('.EventList');
    }
}
